import {
  CellStyle,
  ColDef,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { inject, Injectable } from "@angular/core";
import { BulkUploadAsinCellRendererComponent } from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-asin-cell-renderer.component";
import { BulkUploadCellRendererComponent } from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-cell-renderer.component";
import { BulkUploadTypeIconGridComponent } from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon-grid.component";
import { StrategyLinkGridComponent } from "@m19-board/shared/strategy-link-grid/strategy-link-grid.component";
import { BulkUploadFieldPreview, BulkUploadRowPreview, BulkUploadRunResultItem } from "./bulk-upload-context.service";
import { TranslocoService } from "@jsverse/transloco";

@Injectable({
  providedIn: "root",
})
export class BulkUploadGridUtilsService {
  private readonly translocoService = inject(TranslocoService);

  private readonly uploadTypeColDef: {
    pinned: "left";
    cellStyle: CellStyle;
    headerClass: string;
    cellClass: string;
  } = {
    pinned: "left",
    cellStyle: {
      "--ag-cell-horizontal-padding": "0",
      "--ag-cell-vertical-padding": "0",
      "--ag-cell-horizontal-border": "solid var(--ag-border-color)",
    },
    headerClass: "border-r border-r-[var(--ag-border-color)]",
    cellClass: "!flex !items-center !justify-center",
  };

  public getRunResultGridColumnsDefs<T>(
    config: ReadonlyArray<{ field: keyof T; title: string }>,
    nameField: keyof T,
    idField: keyof T,
  ): ColDef<BulkUploadRunResultItem<T>>[] {
    return [
      this.getUploadTypeRunResultColumnDef<T>(),
      this.getStrategyNameColumnDef<T, BulkUploadRunResultItem<T>>(nameField, idField),
      ...config.map((field) => this.getFieldColumnDefRunResult<T>(field.field, field.title, nameField)),
    ];
  }

  public getPreviewGridColumnsDefs<T>(
    config: ReadonlyArray<{ field: keyof T; title: string }>,
    nameField: keyof T,
  ): ColDef<BulkUploadRowPreview<T>>[] {
    return [
      this.getUploadTypePreviewColumnDef<T>(),
      ...config.map((field) => this.getFieldColumnDefPreview<T>(field.field, field.title, nameField)),
    ];
  }

  public getGridOptions<T>(rowData: T[], columnDefs: ColDef<T>[]): GridOptions<T> {
    return {
      sideBar: false,
      suppressHeaderFocus: true,
      suppressRowHoverHighlight: true,
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
      },
      suppressCsvExport: true,
      suppressColumnVirtualisation: true,
      rowData: rowData,
      columnDefs: columnDefs,
      onGridReady: (params) => {
        params.api.autoSizeAllColumns();
      },
    };
  }

  public getStrategyNameColumnDef<T, TRow extends { parsedEntity: Partial<T>; resultEntity?: T | null }>(
    nameField: keyof T,
    idField: keyof T,
  ): ColDef<TRow> {
    return {
      headerName: this.translocoService.translate("common.strategy_name"),
      pinned: "left",
      cellClass: "!flex !items-center",
      maxWidth: 200,
      valueGetter: (params: ValueGetterParams<TRow, number>) => {
        const entity = params.data?.resultEntity ?? params.data?.parsedEntity;
        return entity?.[idField];
      },
      valueFormatter: (params: ValueFormatterParams<TRow, number>) => {
        const entity = params.data?.resultEntity ?? params.data?.parsedEntity;
        return entity?.[nameField] as string;
      },
      cellRendererSelector: (params: ICellRendererParams<TRow, number>) => {
        const entity = params.data?.resultEntity ?? params.data?.parsedEntity;
        const strategyId = entity?.[idField];
        if (typeof strategyId !== "number" || strategyId === 0) return undefined;
        return {
          component: StrategyLinkGridComponent,
          params: { strategyId },
        };
      },
    };
  }

  private getUploadTypePreviewColumnDef<T>(): ColDef<BulkUploadRowPreview<T>> {
    return {
      cellRenderer: BulkUploadTypeIconGridComponent,
      ...this.uploadTypeColDef,
    };
  }

  private getUploadTypeRunResultColumnDef<T>(): ColDef<BulkUploadRunResultItem<T>> {
    return {
      cellRenderer: BulkUploadTypeIconGridComponent,
      cellRendererParams: (params: ICellRendererParams<BulkUploadRunResultItem<T>>) => {
        return { tooltip: params.data?.error };
      },
      ...this.uploadTypeColDef,
    };
  }

  private getFieldColumnDefPreview<T>(
    field: keyof T,
    headerName: string,
    entityNameField: keyof T,
  ): ColDef<BulkUploadRowPreview<T>, BulkUploadFieldPreview<T>> {
    return {
      headerName: headerName,
      colId: field.toString(),
      valueGetter: (params: ValueGetterParams<BulkUploadRowPreview<T>, BulkUploadFieldPreview<T>>) =>
        params.data?.fieldsPreview.find((f) => f.field === field),
      cellRenderer: field === "asins" ? BulkUploadAsinCellRendererComponent : BulkUploadCellRendererComponent,
      pinned: field === entityNameField ? "left" : undefined,
      maxWidth: field === entityNameField ? 200 : undefined,
    };
  }

  private getFieldColumnDefRunResult<T>(
    field: keyof T,
    headerName: string,
    entityNameField: keyof T,
  ): ColDef<BulkUploadRunResultItem<T>> {
    return {
      headerName: headerName,
      colId: field.toString(),
      valueGetter: (params: ValueGetterParams<BulkUploadRunResultItem<T>>) =>
        params.data?.error ? params.data.parsedEntity[field] : params.data?.resultEntity?.[field],
      cellRenderer: field === "asins" ? BulkUploadAsinCellRendererComponent : undefined,
      pinned: field === entityNameField ? "left" : undefined,
      maxWidth: field === entityNameField ? 200 : undefined,
    };
  }
}
